/**
 * One match on screen: link (online socket or offline worker) → store → renderer + HUD,
 * with the input controller turning clicks and keys into intents.
 */
import type { LoadedWorld } from '@sa/mapdata';
import { cityNames, decodeSnapshot, type EndMessage, type WelcomeMessage } from '@sa/protocol';
import { BALANCE, TICK_HZ, getGeo, lonLatToMap, landHexAt, type EntityView, type Intent, type PlayerId, type PlayerInfo } from '@sa/sim';
import type { Music } from '../audio/music';
import type { Sfx } from '../audio/sfx';
import { params } from '../config';
import { TickClock } from '../net/clock';
import { Camera } from '../input/camera';
import { KEY, braceAction, chipFor, samFocusIntents, samsReaching, type ChipAction } from '../input/actions';
import { cityIncome, incomeBreakdown } from '../state/economy';
import { InputController } from '../input/controller';
import { Palette } from '../render/palette';
import { buildHexLookup, type HexLookup } from '../render/hexfield';
import { Renderer, StageProfiler } from '../render/renderer';
import { briefing } from '../state/score';
import { soundtrackCue } from '../state/soundtrack';
import { offerCards, shownOffer } from '../state/toasts';
import { fallenAllyCard } from '../state/news';
import { Alerts, BRACE_NOW_S, braceCandidate, braceTiming, clock, leftAt, rivalCriticalLine, type AlertLine } from '../state/alerts';
import { launchersFor, strikeOffer } from '../input/commands';
import { GameStore, type InboundThreat } from '../state/store';
import { allyRefusal, diplomacyConfirm, diplomacyTarget, incomingOffer, nationMenu, offerAnswer, type IncomingOffer, type MenuChoice, type MenuItem, type NationMenu } from '../state/diplomacy';
import { allyStakeMatters } from '../state/threats';
import { h, setText as setTextOf } from '../ui/dom';
import { Hud } from '../ui/hud';
import { Guide } from '../state/guide';
import { onLeave, track } from '../net/track';
import type { CloseReason } from '../net/socket';
import { RESUME_WINDOW_MS } from '../net/socket';
import { ackText } from './errors';
import { homeFrame, threatFrame, type Frame } from './framing';
import type { MatchLink } from './link';
import type { ChatMatchHost } from '../ui/chat';

export interface SessionResult {
  end: EndMessage;
  me: PlayerId;
  players: PlayerInfo[];
  /** the match length (ticks): moments are told as the clock showed them (`OUT 9:54 LEFT`) */
  matchTicks: number;
}

export interface SessionOpts {
  world: LoadedWorld;
  link: MatchLink;
  sfx: Sfx;
  music: Music;
  /** match finished (results) or the player left (null) */
  onExit(result: SessionResult | null): void;
  onError(msg: string): void;
  /** shown until the first snapshot arrives */
  loadingText: string;
  /** online: the server's start countdown (the loading text counts it down: MATCH STARTS IN 3, 2, 1) */
  countdownMs?: number;
  /** online: the seat came back after a dropped link */
  onResumed?(): void;
}

/**
 * Online link trouble: `lost` since `since` (ms; reconnecting in the background) or `closed`
 * (the socket gave up: `retry` tries again).
 */
interface LinkDown {
  since: number;
  /** a RETRY is under way: the CONNECTION LOST screen stays down until then (ms) */
  quietUntil: number;
  closed: CloseReason | null;
  retry: (() => void) | null;
  /** the match is gone for good (no RETRY) */
  dead: boolean;
}

const PING_MS = 2000;
/** fallback zoom (css px per mu) before we know our territory */
const DEFAULT_ZOOM = 20;
/** dynamic resolution: frame-time targets (ms) and the lowest GL scale */
const SLOW_FRAME_MS = 28;
const FAST_FRAME_MS = 18;
const MIN_RES_SCALE = 0.5;
/** Space on a threat frames at least this much closer than home */
const THREAT_HOME_ZOOM = 1.3;
/** Space cycles through threats; after this long it starts again at the most urgent one */
const THREAT_CYCLE_RESET_MS = 4000;
/** how often the alert line's SAM-focus offer re-checks which SAMs can reach (ms) */
const SAM_OFFER_EVERY_MS = 500;
/** an open nation menu refreshes this often (countdowns, answered offers) */
const NATION_MENU_EVERY_MS = 500;
/** after this long without the server, the dropped link becomes the CONNECTION LOST screen (still retrying) */
const LOST_SCREEN_MS = 20_000;
const RETRY_QUIET_MS = 6000;


/** Let the browser paint (the loading screen) before the next heavy step. */
const nextPaint = () => new Promise<void>((r) => requestAnimationFrame(() => setTimeout(r, 0)));

export class GameSession {
  readonly el: HTMLDivElement;
  private glCanvas = h('canvas.gl');
  private labelCanvas = h('canvas.labels');
  private cam: Camera;
  private clock: TickClock;
  private store: GameStore | null = null;
  private pal: Palette | null = null;
  private renderer: Renderer | null = null;
  private input: InputController | null = null;
  private hud: Hud | null = null;
  private alerts: Alerts | null = null;
  private welcome: WelcomeMessage | null = null;
  /** the match length (ticks; dev matches may be shorter) */
  private matchTicks = BALANCE.matchTicks;
  private names = new Map<number, string>();
  private raf = 0;
  private last = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pendingLaunch = new Set<number>();
  /** the guided first match (`state/guide.ts`; shown until walked once in this browser) */
  private guide = new Guide();
  /** tracking: our first order of the match went out / the match has started for us */
  private firstOrder = false;
  private tracked = false;
  private offLeave: () => void = () => {};
  /** diplomacy orders waiting for their ack (seq → the order): confirmed in words, a refusal names the right side */
  private pendingDiplo = new Map<number, Intent>();
  /** the open nation menu: whose, opened from the board (offer CENTER CAMERA), last refreshed (ms) */
  private nationAt: { player: PlayerId; board: boolean; ms: number } | null = null;
  private nameOf: (p: PlayerId) => string = (p) => `P${p}`;
  private lastTickSecond = -1;
  private frameMs = 16;
  /** which open offer the offer card shows (› NEXT pages) */
  private offerPage = 0;
  private lastAdapt = 0;
  private boardOpen = false;
  private ended = false;
  private resizeObs: ResizeObserver;
  private loading: HTMLDivElement;
  private loadingText = h('div.t');
  private loadingSub = h('div.sub');
  /** the goal, while the match loads */
  private loadingBrief = h('div.brief');
  private loadingBar = h('b');
  private lookup: HexLookup | null = null;
  private paused = false;
  /** we were eliminated: spectator HUD, free camera */
  private spectating = false;
  /** we asked to surrender (the elimination moment says so) */
  private surrendered = false;
  /** last threat Space glided to (cycling) */
  private lastThreat: { id: number; ms: number } | null = null;
  /** last threat T pointed our SAMs at (cycling) */
  private lastSamFocus: { id: number; ms: number } | null = null;
  /** when the Q STRIKE offer was last refreshed (ms) */
  private strikeAt = -Infinity;
  /** the alert line's "E FOCUS SAMS" offer (re-checked every SAM_OFFER_EVERY_MS) */
  private samOffer: { at: number; action: ChipAction | null } = { at: -Infinity, action: null };
  /** online link trouble (null: fine) */
  private linkDown: LinkDown | null = null;
  private countdown: ReturnType<typeof setInterval> | null = null;
  /** dev screenshots (`until=`): look at where the event happened */
  private devLook = (e: Event) => {
    const { x, y } = (e as CustomEvent<{ x: number; y: number }>).detail;
    // a threat there: the same frame Space gives it (threats are refreshed per frame: catch up first)
    this.store?.updateFrame(this.store.view.tick, this.cam.visibleRect());
    const t = this.store?.inboundThreats.find((t) => Math.hypot(t.target.x - x, t.target.y - y) < 1);
    const f = t && this.threatFrame(t);
    if (f) this.cam.lookAt(f.x, f.y, f.zoom);
    else this.cam.lookAt(x, y);
  };

  constructor(
    container: HTMLElement,
    private o: SessionOpts,
  ) {
    this.loading = h('div.session-loading', {}, this.loadingText, h('div.progress', {}, this.loadingBar), this.loadingSub, this.loadingBrief);
    this.setBrief(BALANCE.matchTicks);
    this.setLoading(o.loadingText, 0);
    if (o.countdownMs) {
      // MATCH STARTS IN 3 · 2 · 1, then the load steps take over
      const end = performance.now() + o.countdownMs;
      const paint = () => {
        const left = Math.ceil((end - performance.now()) / 1000);
        if (this.welcome) {
          if (this.countdown) clearInterval(this.countdown);
        } else this.setLoading(left >= 1 ? `${o.loadingText} ${left}` : 'ESTABLISHING LINK', 0);
      };
      paint();
      this.countdown = setInterval(paint, 200);
    }
    this.el = h('div.session', {}, this.glCanvas, this.labelCanvas, this.loading);
    container.append(this.el);
    this.cam = new Camera(o.world.width, o.world.height);
    this.clock = new TickClock(o.link.tickMs);
    this.resizeObs = new ResizeObserver(() => this.resize());
    this.glCanvas.addEventListener('webglcontextlost', (e) => this.onContextLost(e));
    this.glCanvas.addEventListener('webglcontextrestored', () => this.onContextRestored());
    this.resizeObs.observe(this.el);
    o.link.bind({
      welcome: (w) => this.onWelcome(w),
      snapshot: (b) => this.onSnapshot(b),
      ack: (seq, ok, err) => this.onAck(seq, ok, err),
      pong: (t, tick) => this.clock.onPong(t, performance.now(), tick),
      end: (e) => this.onEnd(e),
      error: (m) => o.onError(m),
    });
    if (!o.link.local) {
      this.pingTimer = setInterval(() => o.link.ping(performance.now()), PING_MS);
      o.link.ping(performance.now());
    }
  }

  get isOffline(): boolean {
    return this.o.link.local;
  }

  /** What the chat panel (ui/chat.ts) needs from the match on screen; null until the match has loaded. */
  chatHost(): ChatMatchHost | null {
    const store = this.store;
    const pal = this.pal;
    if (!store || !pal || !this.welcome) return null;
    return {
      el: this.el,
      canvas: this.glCanvas,
      cam: this.cam,
      world: this.o.world,
      view: () => store.view,
      allies: () => store.allies,
      fallenAlly: () => store.fallenAlly,
      out: () => store.eliminated,
      pal,
      nameOf: (p) => this.nameOf(p),
      toast: (spec) => this.alerts?.toasts.push(spec, performance.now()),
      modal: () => this.hud?.menuOpen ?? false,
    };
  }

  /** The goal on the loading screen (the match length is known once welcomed). */
  private setBrief(matchTicks: number): void {
    this.loadingBrief.replaceChildren(h('div.h', {}, 'MISSION'), ...briefing(matchTicks).map((l) => h('div', {}, l)));
  }

  /** Loading screen: what is happening and how far along (0..1). */
  private setLoading(text: string, progress: number): void {
    setTextOf(this.loadingText, text);
    this.loadingBar.style.width = `${Math.round(progress * 100)}%`;
  }

  private resize(): void {
    const r = this.el.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.cam.resize(r.width, r.height);
    this.renderer?.resize(r.width, r.height, dpr);
  }

  private onWelcome(w: WelcomeMessage): void {
    if (this.countdown) clearInterval(this.countdown);
    this.countdown = null;
    // a second welcome is our seat coming back after a dropped link (a `full` follows)
    if (this.welcome) {
      if (w.matchId === this.welcome.matchId) this.resumed();
      else this.o.onError('Match changed');
      return;
    }
    void this.init(w).catch((e: Error) => this.o.onError(e.message));
  }

  // ------------------------------------------------------------------ online link

  /** The socket dropped (it reconnects by itself; `retry` tries at once): freeze the clock, dim the world, say so. */
  linkLost(since: number, retry: () => void): void {
    if (this.ended) return;
    const prev = this.linkDown && !this.linkDown.dead ? this.linkDown : null;
    // retrying after the socket had given up: give the attempt a moment before CONNECTION LOST again
    const quietUntil = prev?.closed ? performance.now() + RETRY_QUIET_MS : (prev?.quietUntil ?? 0);
    this.linkDown = { since: prev?.since ?? since, quietUntil, closed: null, retry, dead: false };
    this.hud?.hideLost();
    this.el.classList.add('linklost');
    this.hud?.setLinkDown(true);
  }

  /** The socket gave up (or the server closed it on us): CONNECTION LOST with RETRY / MENU. */
  linkClosed(why: CloseReason, retry: () => void): void {
    if (this.ended) return;
    this.linkDown = { since: this.linkDown?.since ?? performance.now(), quietUntil: 0, closed: why, retry: why === 'unauthorized' ? null : retry, dead: false };
    this.el.classList.add('linklost');
    this.hud?.setLinkDown(true);
    this.showLost();
  }

  /** The match cannot be resumed (it is over, or our seat was released): only MENU. */
  linkDead(title: string, sub: string): void {
    if (this.ended || this.o.link.local || !this.welcome) return;
    this.linkDown = { since: this.linkDown?.since ?? performance.now(), quietUntil: 0, closed: 'lost', retry: null, dead: true };
    this.el.classList.add('linklost');
    this.hud?.setLinkDown(true);
    this.hud?.showLost(title, sub, [{ label: 'MENU', run: () => this.exit(null), primary: true }]);
  }

  /** The CONNECTION LOST screen for the current link trouble (its seat-hold countdown is refreshed per frame). */
  private showLost(): void {
    const d = this.linkDown;
    if (!d || d.dead || !this.hud) return;
    const menu = { label: 'MENU', run: () => this.exit(null) };
    const retry = d.retry ? [{ label: d.closed === 'replaced' ? 'PLAY HERE' : 'RETRY', run: d.retry, primary: true }] : [];
    if (d.closed === 'replaced') this.hud.showLost('PLAYING IN ANOTHER TAB', 'THIS ACCOUNT CONNECTED FROM SOMEWHERE ELSE', [menu, ...retry]);
    else if (d.closed === 'unauthorized') this.hud.showLost('SESSION EXPIRED', 'SIGN IN AGAIN FROM THE MENU', [{ ...menu, primary: true }]);
    else {
      const left = (RESUME_WINDOW_MS - (performance.now() - d.since)) / 1000;
      const sub = d.closed || left <= 0 ? 'THE SERVER IS NOT ANSWERING' : `RECONNECTING · YOUR SEAT IS HELD ${clock(left)}`;
      this.hud.showLost('CONNECTION LOST', sub, [menu, ...retry]);
    }
  }

  /** The alert line while the link is down: `LINK LOST · RECONNECTING 0:07` (the world is dimmed and frozen). */
  private linkLine(now: number): AlertLine {
    const d = this.linkDown!;
    const secs = (now - d.since) / 1000;
    return { text: d.closed || d.dead ? 'LINK LOST' : `LINK LOST · RECONNECTING ${clock(secs)}`, tone: 'calm' };
  }

  /** Our seat is back (welcome + full after a reconnect). */
  private resumed(): void {
    this.clock.reset();
    this.o.onResumed?.();
    if (!this.linkDown) return;
    this.linkDown = null;
    this.el.classList.remove('linklost');
    this.hud?.setLinkDown(false);
    this.hud?.hideLost();
    this.alerts?.say('LINK RESTORED', 'ok', performance.now(), 1500);
  }

  /**
   * Set the match up in steps, letting the loading screen paint (and its bar move) between the
   * heavy ones: map lookups, GL programs, then the first snapshot and the first drawn frame.
   */
  private async init(w: WelcomeMessage): Promise<void> {
    const world = this.o.world;
    this.welcome = w;
    const prof = params.prof ? (label: string, t0: number) => console.warn(`[load] ${label} ${(performance.now() - t0).toFixed(0)}ms`) : null;
    // the server may run faster than 10 Hz (dev `simSpeed`): pace the tick clock by its real rate
    if (!this.o.link.local && w.tickRate > 0) this.clock = new TickClock(1000 / w.tickRate);
    const players: PlayerInfo[] = w.players.map((p) => ({ ...p }));
    this.names = cityNames(world, players);
    this.o.music.newMatch();
    this.store = new GameStore(world, { me: w.you.playerId, team: w.you.team, mode: w.settings.mode, players, landCount: world.landCount }, this.names);
    this.pal = new Palette(w.you.playerId, players);
    const nameOf = (p: PlayerId) => (players.find((q) => q.id === p)?.name ?? `P${p}`).toUpperCase();
    this.nameOf = nameOf;
    const matchTicks = w.settings.matchTicks ?? BALANCE.matchTicks;
    this.matchTicks = matchTicks;
    this.setBrief(matchTicks);
    this.alerts = new Alerts(this.store, nameOf, matchTicks);
    this.alerts.guided = this.guide.showing;
    if (this.loading.isConnected) {
      const region = world.regions.find((r) => r.id === players.find((p) => p.id === w.you.playerId)?.regionId);
      setTextOf(this.loadingSub, `${region?.name ?? ''} · ${players.length} NATIONS`);
    }
    const store = this.store;
    store.on((e) => {
      const now = performance.now();
      this.alerts!.onEvent(e, now);
      const sfx = this.o.sfx;
      // a spectator watches: no alarms, no contact pings
      const watching = store.view.eliminated;
      if (e.type === 'track') e.alarm ? sfx.inbound() : watching || sfx.ping();
      else if (e.type === 'hint') watching || sfx.hint();
      else if (e.type === 'detonation') {
        sfx.detonation(e.mine);
        this.o.music.hit(e.mine);
      } else if (e.type === 'lost' && e.kind === 'city') this.o.music.cityLost();
      else if (e.type === 'intercept') sfx.intercept(e.hit);
      // (news and offers sound as their toast comes up: `toastSounds`)
      else if (e.type === 'critical' && e.on && e.fresh) e.player === store.me ? sfx.inbound() : sfx.ping();
      else if (e.type === 'eliminated' && e.player === store.me) this.knockedOut(e.decapitated, e.by, e.tick, nameOf);
      // our friend fell: the ALLY line stays (online, a human: someone to talk to)
      else if (e.type === 'allyFell' && !e.us && !this.isOffline && !store.view.players.find((p) => p.id === e.ally)?.isBot) this.alerts!.toasts.push(fallenAllyCard(e.ally, nameOf(e.ally)), now);
    });
    this.setLoading('PREPARING MAP', 0.25);
    await nextPaint();
    let t0 = performance.now();
    this.lookup = buildHexLookup(world);
    prof?.('hex lookup', t0);
    this.setLoading('PREPARING DISPLAY', 0.5);
    await nextPaint();
    t0 = performance.now();
    this.makeRenderer();
    prof?.('renderer', t0);
    this.setLoading(store.started ? 'DRAWING' : 'WAITING FOR THE WORLD', 0.75);
    this.hud = new Hud(this.pal, players, matchTicks, {
      build: (k) => this.input?.startBuild(k),
      action: (a) => this.runAction(a),
      nationAct: (item, choice) => this.nationAct(item, choice),
      nationRow: (p, x, y) => this.openNation(p, x, y, true),
      nationHover: (p) => {
        if (p === null) store.highlight = null;
        else if (store.highlight?.player !== p) store.highlight = { player: p, atMs: performance.now() };
      },
      resume: () => this.setMenu(false),
      toggleMute: () => this.o.sfx.toggleMute(),
      cycleMusic: () => this.o.music.cycle(),
      surrender: () => {
        track('surrender', { s: this.matchSecs() });
        this.surrendered = true;
        this.send({ type: 'surrender' });
        this.setMenu(false);
      },
      leave: () => {
        if (!this.ended) track('match_leave', { s: this.matchSecs(), via: this.store?.view.eliminated ? 'spectating' : 'menu' });
        this.exit(null);
      },
      alertClicked: () => this.jumpToThreat(),
      error: (msg) => this.refuse(msg),
      chipTitle: (click) => this.pointAtSelection(click),
      income: () => incomeBreakdown(store.view, world),
      skipGuide: () => {
        track('guide_skip', { step: this.guide.progress.length + 1, s: this.matchSecs() });
        this.guide.skip();
      },
      cityName: (id) => {
        const c = store.view.entities.get(id);
        return c ? (this.names.get(landHexAt(getGeo(world), c.x, c.y)) ?? null) : null;
      },
    });
    this.el.append(this.hud.root);
    this.hud.setNamed(this.guide.active);
    this.input = new InputController(this.glCanvas, this.cam, store, world, this.lookup, {
      send: (i) => this.send(i),
      nationHold: (owner, sx, sy) => this.openNation(owner, sx, sy, false),
      holdCue: (at) => this.hud?.showHold(at),
      dismiss: () => this.hud?.closeNation() ?? false,
      // (Esc inside RULES · CONTROLS goes back to the menu)
      menu: () => this.hud?.menuBack() || this.setMenu(!this.hud?.menuOpen),
      modal: () => this.hud?.menuOpen ?? false,
      hotkey: (k) => this.hotkey(k),
      toggleBoard: (show) => {
        this.boardOpen = show;
        this.hud?.showBoard(show, store.view, store.allies, store.eliminated, store.diplomacy);
      },
      feedback: (kind, msg) => {
        this.o.sfx.unlock();
        if (kind === 'error') this.refuse(msg ?? 'INVALID');
        else if (kind === 'warn') {
          this.alerts?.warn(msg ?? '', performance.now());
          this.o.sfx.error();
        } else {
          // what we just ordered (ICBM AWAY · KAZAN, BOMBERS → …): confirmed by the pointer, briefly
          if (msg) this.hud?.flash(msg);
          this.o.sfx.click();
        }
        this.hud?.closeNation();
      },
    });
    this.glCanvas.addEventListener('pointerdown', () => this.o.sfx.unlock());
    if (import.meta.env.DEV) {
      window.addEventListener('sa:devlook', this.devLook);
      // UI flow scripts (Playwright) read positions and state through this
      (window as unknown as { __sa: unknown }).__sa = { store, cam: this.cam, input: this.input, alerts: this.alerts };
    }
    this.resize();
    this.home();
    this.hud.root.classList.add('pre');
    this.last = performance.now();
    this.raf = requestAnimationFrame((t) => this.frame(t));
    // knocked out before we were set up (a reconnect): straight to spectating
    if (store.view.eliminated) {
      this.spectating = false;
      this.enterSpectator();
    }
    this.liftLoading();
  }

  /**
   * The world is drawn for the first time underneath the loading screen (the first frame builds
   * coastline detail and is the slowest); the screen lifts on the next one.
   */
  private lifting = false;
  private liftLoading(): void {
    if (!this.loading.isConnected || this.lifting || !this.renderer || !this.store?.started) return;
    this.lifting = true;
    this.setLoading('DRAWING', 0.9);
    this.home();
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.loading.remove();
        this.trackStart();
        this.lifting = false;
        this.hud?.root.classList.remove('pre');
      }),
    );
  }

  /**
   * We are out: the full-screen moment ("CAPITAL DESTROYED — ELIMINATED · 5:38 LEFT · BY VOLGA"),
   * then the spectator HUD with the whole world in view.
   */
  private knockedOut(decapitated: boolean, by: PlayerId, tick: number, nameOf: (p: PlayerId) => string): void {
    track('eliminated', { s: Math.round(tick / TICK_HZ), capital: decapitated });
    const title = decapitated ? 'CAPITAL DESTROYED' : this.surrendered ? 'SURRENDERED' : 'ELIMINATED';
    const who = by ? ` · BY ${nameOf(by)}` : '';
    this.hud?.showEliminated(title, `ELIMINATED · ${leftAt(tick, this.matchTicks)}${who}`);
    this.o.sfx.detonation(true);
    this.enterSpectator();
  }

  private enterSpectator(): void {
    if (this.spectating) return;
    this.spectating = true;
    this.input?.setMode({ type: 'idle' });
    this.input?.select([]);
    this.hud?.setSpectating(true);
    this.home(true);
  }

  /**
   * The chip title was pointed at: the selection marker pulses again; clicked, the camera also
   * glides to the selection when none of it is on screen (zoom kept).
   */
  private pointAtSelection(click: boolean): void {
    const input = this.input;
    if (!input) return;
    // (hovering: once per pulse — the chip re-renders under a resting pointer)
    if (!click && performance.now() - input.selectedAt < 1500) return;
    input.flashSelection();
    if (!click) return;
    const pos = input.selection().map((e) => input.lastScene?.pos.get(e.id) ?? { x: e.x, y: e.y });
    if (!pos.length) return;
    const [x0, y0, x1, y1] = this.cam.visibleRect(-80);
    if (pos.some((p) => p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1)) return;
    this.cam.flyTo(pos.reduce((a, p) => a + p.x, 0) / pos.length, pos.reduce((a, p) => a + p.y, 0) / pos.length);
  }

  /** An invalid action: say why on the alert line. */
  private refuse(msg: string): void {
    this.alerts?.error(msg, performance.now());
    this.o.sfx.error();
  }

  /**
   * Esc menu: modal (all game input is swallowed behind its scrim). Offline matches pause
   * underneath it (the world freezes at the last tick); online the match keeps running.
   */
  private setMenu(open: boolean): void {
    const v = this.store?.view;
    if (!this.hud || !v) return;
    if (open) {
      this.input?.setMode({ type: 'idle' });
      this.hud.closeNation();
      this.boardOpen = false;
      this.hud.showBoard(false, v, [], new Map());
    }
    this.hud.showMenu(open, this.o.sfx.muted, this.o.music.label, !v.eliminated && !this.ended, !!this.o.link.pause);
    if (this.o.link.pause) {
      this.paused = open;
      this.o.link.pause(open);
      if (!open) this.clock.reset();
    }
  }

  /** (Re)create all GL resources; also used after the GPU context was lost and restored. */
  private makeRenderer(): void {
    try {
      const r = new Renderer(this.glCanvas, this.labelCanvas, this.o.world, this.names, this.lookup!);
      if (params.prof) r.prof = new StageProfiler();
      if (r.software) r.resolutionScale = MIN_RES_SCALE;
      r.skip = params.skipStages;
      this.renderer = r;
      if (this.store) this.store.hexDirty = this.store.flipDirty = true;
      this.resize();
    } catch (e) {
      // a lost context fails shader compilation; we retry on `webglcontextrestored`
      this.renderer = null;
      if (!this.glCanvas.getContext('webgl2')?.isContextLost()) this.o.onError((e as Error).message);
    }
  }

  private onContextLost(e: Event): void {
    e.preventDefault();
    this.renderer = null;
    this.setLoading('GRAPHICS RESET', 0);
    if (!this.loading.isConnected) this.el.append(this.loading);
  }

  private onContextRestored(): void {
    this.makeRenderer();
    if (this.renderer) this.loading.remove();
  }

  /**
   * Glide to an inbound threat's impact point. `cycle` (Space / alert click again within a
   * few seconds) steps to the next threat by ETA; otherwise the most urgent one.
   */
  private jumpToThreat(cycle = true): boolean {
    // ours first, then an ally's city / capital / silo (their open ground is not ours to watch)
    const th = [...(this.store?.inboundThreats ?? []), ...(this.store?.allyThreats ?? []).filter(allyStakeMatters)];
    if (!th.length) return false;
    const now = performance.now();
    const last = this.lastThreat;
    let i = 0;
    if (cycle && last && now - last.ms < THREAT_CYCLE_RESET_MS) {
      const j = th.findIndex((t) => t.id === last.id);
      i = j < 0 ? 0 : (j + 1) % th.length;
    }
    const t = th[i];
    this.lastThreat = { id: t.id, ms: now };
    const f = this.threatFrame(t);
    this.cam.flyTo(f.x, f.y, f.zoom);
    return true;
  }

  /** The camera frame for a threat: its target and missile head, zoomed in at least past home. */
  private threatFrame(t: InboundThreat): Frame {
    const store = this.store!;
    const { w, h } = this.cam.vp;
    const home = homeFrame(store.view, this.o.world, w, h, this.cam.minZoom * 1.4, store.capitalHex, this.cam.minZoom)?.zoom ?? DEFAULT_ZOOM;
    return threatFrame(t.target, t.head, w, h, home * THREAT_HOME_ZOOM);
  }

  /** Frame our nation (territory + radar cover); dev `look=` / `zoom=` override it. Spectators see the world. */
  private home(glide = false): void {
    const w = this.welcome!;
    const world = this.o.world;
    const minZ = this.cam.minZoom * 1.4;
    if (this.store?.view.eliminated || this.ended) {
      this.cam.flyTo(world.width / 2, world.height / 2, this.cam.minZoom * 1.15);
      return;
    }
    const focus = this.store?.view.focus;
    if (params.look === 'focus' && focus !== null && focus !== undefined) {
      this.cam.lookAt(world.cx[focus], world.cy[focus], params.zoom ?? DEFAULT_ZOOM);
      return;
    }
    if (Array.isArray(params.look)) {
      const p = lonLatToMap(params.look[0], params.look[1]);
      this.cam.lookAt(p.x, p.y, params.zoom ?? DEFAULT_ZOOM);
      return;
    }
    const f = this.store?.started ? homeFrame(this.store.view, world, this.cam.vp.w, this.cam.vp.h, minZ, this.store.capitalHex, this.cam.minZoom) : null;
    const me = w.players.find((p) => p.id === w.you.playerId);
    const reg = world.regions.find((r) => r.id === me?.regionId);
    const x = f?.x ?? (reg ? world.cx[reg.capitalHex] : this.cam.cx);
    const y = f?.y ?? (reg ? world.cy[reg.capitalHex] : this.cam.cy);
    const z = params.zoom ?? f?.zoom ?? Math.max(minZ, DEFAULT_ZOOM);
    if (glide) this.cam.flyTo(x, y, z);
    else this.cam.lookAt(x, y, z);
  }

  private onSnapshot(buf: ArrayBuffer): void {
    if (!this.store) return;
    const snap = decodeSnapshot(buf);
    const now = performance.now();
    this.store.apply(snap, now / 1000);
    this.clock.onSnapshot(snap.hdr.tick, now, this.o.link.local);
    // a spectator has no side: every nation in its own colour
    this.pal!.setAllies(this.store.allies);
    this.liftLoading();
    // a reconnect (or a late join) after we were knocked out: straight to spectating
    if (this.store.view.eliminated && !this.spectating) this.enterSpectator();
    if (this.boardOpen) this.hud?.showBoard(true, this.store.view, this.store.allies, this.store.eliminated, this.store.diplomacy);
  }

  /** Send an order; its seq (null: not sent, the link is down). */
  private send(i: Intent): number | null {
    // no server, no orders (they would vanish without an answer)
    if (this.linkDown) {
      this.refuse('LINK LOST · ORDERS PAUSED');
      return null;
    }
    const seq = this.o.link.send(i);
    if (!this.firstOrder) {
      this.firstOrder = true;
      track('first_input', { type: i.type, s: this.matchSecs() });
    }
    if (i.type === 'launch') this.pendingLaunch.add(seq);
    if (diplomacyTarget(i) !== null) this.pendingDiplo.set(seq, i);
    return seq;
  }

  private onAck(seq: number, ok: boolean, err?: string): void {
    const launch = this.pendingLaunch.delete(seq);
    const diplo = this.pendingDiplo.get(seq);
    this.pendingDiplo.delete(seq);
    const store = this.store;
    if (ok) {
      if (launch) {
        this.o.sfx.launch();
        this.guide.launched();
      }
      if (diplo && store) {
        if (diplo.type === 'gift') store.diplomacy.noteGift(diplo.to, diplo.amount);
        // (SENT 1,000 TO KAZAN, CEASEFIRE PROPOSED · VULCAN: confirmed by the pointer, not in the news)
        const words = diplomacyConfirm(diplo, this.nameOf, store.allies);
        if (words) this.hud?.flash(words);
      }
      return;
    }
    const other = diplo ? diplomacyTarget(diplo) : null;
    const words = other !== null && store ? allyRefusal(err, other, store.view, store.diplomacy, store.allies, this.nameOf) : null;
    this.refuse(words ?? ackText(err));
  }

  /**
   * Open the nation menu for `p` at screen (sx, sy): held right-click on their land, or their row in
   * the Tab board (`board`: then CENTER CAMERA is offered when their capital is off screen).
   */
  private openNation(p: PlayerId, sx: number, sy: number, board: boolean): void {
    const store = this.store;
    if (!store || !this.hud || store.view.eliminated || this.ended) return;
    this.alerts?.tips.complete('diplo');
    this.nationAt = { player: p, board, ms: performance.now() };
    this.o.sfx.click();
    this.hud.showNation(this.nationModel(p, board), sx, sy);
  }

  private nationModel(p: PlayerId, board: boolean): NationMenu {
    const store = this.store!;
    const v = store.view;
    const world = this.o.world;
    const hex = store.capitalOf(p);
    // STRIKE CAPITAL: our loaded launchers that reach it, while the capital (as far as we know) is still theirs
    const launchers = hex >= 0 && v.owner[hex] === p ? launchersFor(v, world.cx[hex], world.cy[hex]).length : 0;
    let offscreen = false;
    if (board && hex >= 0) {
      const [x0, y0, x1, y1] = this.cam.visibleRect();
      offscreen = world.cx[hex] < x0 || world.cx[hex] > x1 || world.cy[hex] < y0 || world.cy[hex] > y1;
    }
    return nationMenu({ view: v, world, book: store.diplomacy, allies: store.allies, nameOf: this.nameOf, launchers, offscreen, matchTicks: this.matchTicks }, p);
  }

  /** A nation menu row (or a SEND CREDITS amount) was chosen. */
  private nationAct(item: MenuItem, choice?: MenuChoice): void {
    const p = this.hud?.nationOpen ?? this.nationAt?.player;
    const store = this.store;
    if (p === null || p === undefined || !store) return;
    this.o.sfx.click();
    const hex = store.capitalOf(p);
    if (item.id === 'center') {
      if (hex >= 0) this.cam.flyTo(this.o.world.cx[hex], this.o.world.cy[hex]);
      return;
    }
    if (item.id === 'strike') {
      if (hex < 0) return;
      const x = this.o.world.cx[hex];
      const y = this.o.world.cy[hex];
      this.aimAtCapital(x, y, hex, launchersFor(store.view, x, y));
      return;
    }
    const intent = choice?.intent ?? item.intent;
    if (!intent) return;
    if (intent.type === 'ally.respond' || intent.type === 'ceasefire.respond') {
      const o = incomingOffer(store.view, store.diplomacy);
      const kind = intent.type === 'ally.respond' ? 'ally' : 'ceasefire';
      const r = (kind === 'ally' ? store.view.allyRequests : store.view.ceasefireRequests).find((x) => x.from === intent.from && x.to === store.me);
      this.answerOffer(o && o.kind === kind && o.from === intent.from ? o : { kind, from: intent.from, expiresTick: r?.expiresTick ?? store.view.tick }, intent.accept, false);
      return;
    }
    this.send(intent);
  }

  /**
   * Answer an offer to us (Y / N, the card's buttons, the nation menu). The card leaves at once —
   * it is never asked twice; the answer is confirmed by the pointer (ALLIANCE ACCEPTED · VULCAN), and
   * a refusal from the server follows on the alert line (OFFER EXPIRED, VULCAN ALREADY HAS AN ALLY).
   */
  private answerOffer(o: IncomingOffer, accept: boolean, click = true): void {
    const store = this.store;
    if (!store) return;
    if (click) this.o.sfx.click();
    if (this.send(offerAnswer(o, accept)) === null) return;
    store.diplomacy.answer(o);
    const what = o.kind === 'ally' ? 'ALLIANCE' : 'CEASEFIRE';
    if (accept) this.alerts?.accepted(o.kind, o.from, performance.now());
    this.hud?.flash(`${what} ${accept ? 'ACCEPTED' : 'DECLINED'} · ${this.nameOf(o.from)}`);
  }

  private runAction(a: ChipAction): void {
    if (!a.enabled) {
      this.refuse(a.why ?? 'NOT AVAILABLE');
      return;
    }
    this.o.sfx.click();
    if (a.run) a.run();
    else if (a.intent && !Array.isArray(a.intent) && a.intent.type === 'brace') this.brace(a.intent.cityId);
    else if (a.intent) for (const i of Array.isArray(a.intent) ? a.intent : [a.intent]) this.send(i);
    else if (a.aim) this.input?.startAim(a.aim);
  }

  /**
   * Brace a city (G, its chip, the alert line). A brace lasts 20 s: pressed earlier than that
   * before the impact it is armed instead (`BRACE ARMED · ACTIVE AT T-0:19`) and sent in time by
   * `updateBraceArms` — the 300 is paid then, and not at all if the threat is gone by then.
   */
  private brace(cityId: number): void {
    const store = this.store!;
    const t = braceTiming(store.inboundThreats, cityId);
    if (t && t.sendIn > BRACE_NOW_S) {
      store.braceArms.add(cityId);
      this.alerts?.confirm(`BRACE ARMED · ACTIVE AT T-${clock(t.activeAt)}`, performance.now());
      return;
    }
    store.braceArms.delete(cityId);
    this.send({ type: 'brace', cityId });
  }

  /** Armed braces go out when their time comes (or stand down when the threat is gone). */
  private updateBraceArms(): void {
    const store = this.store!;
    for (const id of store.braceArms) {
      const city = store.view.entities.get(id);
      const t = city && city.owner === store.me ? braceTiming(store.inboundThreats, id) : null;
      if (t && t.sendIn > 0) continue;
      store.braceArms.delete(id);
      if (!city) continue;
      if (!t) this.alerts?.say('BRACE STOOD DOWN · NO CHARGE', 'info', performance.now(), 3000);
      else {
        const a = braceAction(store.view, city);
        if (a.enabled) this.send({ type: 'brace', cityId: id });
        else this.refuse(`BRACE FAILED · ${a.why ?? 'NOT AVAILABLE'}`);
      }
    }
  }

  private chip() {
    const store = this.store!;
    const geo = getGeo(this.o.world);
    return chipFor(
      store.view,
      this.input!.selection(),
      (e: EntityView) => this.names.get(landHexAt(geo, e.x, e.y)) ?? null,
      (e: EntityView) => cityIncome(store.view, this.o.world, e),
      { braceArms: store.braceArms, rippling: new Set(this.input!.ripples.keys()), stopRipple: (ids) => this.input?.stopRipple(ids) },
    );
  }

  private hotkey(k: string): boolean {
    const v = this.store?.view;
    if (!v) return false;
    if (k === 'home') {
      if (!this.jumpToThreat()) this.home(true);
      return true;
    }
    if (k === 'm') {
      this.o.sfx.toggleMute();
      return true;
    }
    // a spectator, or a match that is over, takes no orders
    if (v.eliminated || this.ended) return false;
    // Q while aiming a launch: toggle the ripple (even while the launchers reload)
    const mode = this.input?.mode;
    if (k === KEY.attack && mode?.type === 'aim' && mode.action === 'launch') {
      this.input!.startAim('launch');
      this.o.sfx.click();
      return true;
    }
    // Y / N answer the offer the card shows (it pages: › NEXT)
    if (k === 'y' || k === 'n') {
      const o = shownOffer(v, this.store!.diplomacy, this.offerPage)?.offer;
      if (o) {
        this.answerOffer(o, k === 'y');
        return true;
      }
    }
    const chip = this.chip();
    const a = chip?.actions.find((x) => x.key === k);
    if (a) {
      this.runAction(a);
      return true;
    }
    if (k === KEY.attack) {
      if (this.store!.strikeOffer) this.strikeRival();
      // before release, the answer is when
      else if (!v.released) this.refuse(`WEAPONS RELEASE IN ${clock((BALANCE.phases.releaseTick - v.tick) / TICK_HZ)}`);
      else this.refuse('SELECT A SILO OR SUB TO LAUNCH');
      return true;
    }
    // a unit's keys with nothing selected: say where they live (never silence)
    if (!chip && (k === KEY.buy || k === KEY.buy2 || k === KEY.toggle || k === KEY.cancel)) {
      this.refuse('SELECT A UNIT · KEYS ON ITS BAR');
      return true;
    }
    if (k === KEY.defend) {
      this.samFocus();
      return true;
    }
    if (k === KEY.special) {
      // R braces the city under the most urgent threat (or the selected city, via its chip);
      // with nothing tracked yet, a critical capital (the line says R BRACE READY)
      const capital = v.criticalCapitals.has(v.me) ? this.store!.ownCapitalCity() : undefined;
      const brace = braceCandidate(this.store!.inboundThreats, v, this.store!.braceArms) ?? (capital ? braceAction(v, capital) : null);
      if (brace) this.runAction(brace);
      else this.refuse(this.store!.braceArms.size ? 'BRACE ALREADY ARMED' : 'NO CITY UNDER THREAT · SELECT A CITY TO BRACE');
      return true;
    }
    return false;
  }

  /**
   * Q STRIKE on a rival's critical capital: every launcher of ours that reaches it is selected,
   * the camera glides to it and we aim — one click on it fires (Q again: ripple).
   */
  private strikeRival(): void {
    const o = this.store?.strikeOffer;
    if (!o) return;
    this.o.sfx.click();
    this.aimAtCapital(o.x, o.y, o.hex, o.launchers);
  }

  /** Select these launchers, glide to the capital at (x, y) and aim: one click on it fires (Q again: ripple). */
  private aimAtCapital(x: number, y: number, hex: number, launchers: readonly EntityView[]): void {
    if (!launchers.length) {
      this.refuse('NO LAUNCHER IN RANGE');
      return;
    }
    this.input!.aimWith(launchers.map((e) => e.id));
    this.cam.flyTo(x, y);
    const name = this.names.get(hex) ?? 'THE CAPITAL';
    this.alerts?.confirm(`${launchers.length > 1 ? `${launchers.length} LAUNCHERS` : 'LAUNCHER'} ON ${name} · CLICK IT TO FIRE`, performance.now());
  }

  /** Refresh the Q STRIKE offer (a rival's critical capital we reach) and its alert line. */
  private updateStrikeOffer(now: number, tick: number): void {
    const store = this.store!;
    if (now - this.strikeAt >= SAM_OFFER_EVERY_MS) {
      this.strikeAt = now;
      store.strikeOffer = strikeOffer(store.view, this.o.world, (p) => store.capitalOf(p), (p) => store.isFriendly(p));
    }
    const o = store.strikeOffer;
    const name = o && (this.welcome!.players.find((p) => p.id === o.player)?.name ?? `P${o.player}`).toUpperCase();
    const line = o && name ? rivalCriticalLine(name, (o.until - tick) / TICK_HZ, () => this.strikeRival()) : null;
    // already aiming a launch: Q means "ripple" now, so no Q STRIKE button
    const m = this.input!.mode;
    if (line && m.type === 'aim' && m.action === 'launch') delete line.actions;
    this.alerts!.rivalLine = line;
  }

  /**
   * E with no SAM selected (§1.15 SAM priority in one key): every SAM of ours that can reach the
   * threat engages it first — the threat Space last looked at, else the most urgent one; E again
   * within a few seconds moves on to the next threat. Threats no SAM can reach are skipped.
   */
  private samFocus(): void {
    const store = this.store!;
    const th = store.inboundThreats;
    if (!th.length) {
      this.refuse('NO INBOUND · SELECT A SAM SITE TO SET ITS TARGET');
      return;
    }
    const now = performance.now();
    const at = (last: { id: number; ms: number } | null) => (last && now - last.ms < THREAT_CYCLE_RESET_MS ? th.findIndex((t) => t.id === last.id) : -1);
    const again = at(this.lastSamFocus);
    const looked = at(this.lastThreat);
    const start = again >= 0 ? again + 1 : Math.max(0, looked);
    for (let k = 0; k < th.length; k++) {
      const t = th[(start + k) % th.length];
      const intents = samFocusIntents(store.view, t.id);
      if (!intents.length) continue;
      intents.forEach((i) => this.send(i));
      this.lastSamFocus = { id: t.id, ms: now };
      // seen at once: the engaging SAMs flash their lines to it, the offer turns to SAMS ON IT
      store.samFocus = { threatId: t.id, sams: intents.flatMap((i) => (i.type === 'samPriority' ? [i.samId] : [])), atMs: now };
      this.samOffer.at = -Infinity;
      this.o.sfx.click();
      const next = th.length > 1 ? ' · T NEXT' : '';
      this.alerts?.confirm(`SAMS ×${intents.length} ON ${t.label} · T-${clock(t.etaSeconds)}${next}`, now);
      return;
    }
    this.refuse('NO SAM IN RANGE');
  }

  /**
   * "E FOCUS SAMS" on the alert line, with two or more threats a SAM of ours can reach (no
   * button when none can: E then says NO SAM IN RANGE), and `SAMS ON IT` (done) while the
   * threat E focused is still in flight.
   */
  private samFocusOffer(now: number): ChipAction | null {
    const store = this.store!;
    const th = store.inboundThreats;
    if (th.length < 2 || store.view.eliminated) return null;
    if (now - this.samOffer.at > SAM_OFFER_EVERY_MS) {
      const focused = store.samFocus && th.some((t) => t.id === store.samFocus!.threatId);
      const reach = th.some((t) => samsReaching(store.view, t.id).length > 0);
      let action: ChipAction | null = null;
      if (focused) action = { key: KEY.defend, label: 'SAMS ON IT', enabled: false, done: true };
      else if (reach) action = { key: KEY.defend, label: 'FOCUS SAMS', enabled: true, run: () => this.samFocus() };
      this.samOffer = { at: now, action };
    }
    return this.samOffer.action;
  }

  /** Dynamic resolution: trade GL pixels for frame rate on weak GPUs / huge screens. */
  private adaptResolution(t: number, dtMs: number): void {
    this.frameMs = this.frameMs * 0.95 + dtMs * 0.05;
    if (t - this.lastAdapt < 2000) return;
    this.lastAdapt = t;
    const r = this.renderer!;
    if (this.frameMs > SLOW_FRAME_MS && r.resolutionScale > MIN_RES_SCALE) r.resolutionScale = Math.max(MIN_RES_SCALE, r.resolutionScale * 0.85);
    else if (this.frameMs < FAST_FRAME_MS && r.resolutionScale < 1) r.resolutionScale = Math.min(1, r.resolutionScale * 1.1);
  }

  private frame(t: number): void {
    this.raf = requestAnimationFrame((n) => this.frame(n));
    const dt = Math.min(0.1, (t - this.last) / 1000);
    const dtMs = t - this.last;
    this.last = t;
    const store = this.store!;
    if (!store.started || !this.renderer) return;
    this.adaptResolution(t, dtMs);
    this.input!.update(dt);
    this.cam.update(dt);
    // paused, over, or no link: the world (and its clock) stands still at the last tick we know
    const tick = this.ended || this.paused || this.linkDown ? store.view.tick : this.clock.now(t);
    const ov = this.input!.overlay();
    store.updateFrame(tick, this.cam.visibleRect());
    this.o.music.set(soundtrackCue(store.view, tick, store.inboundThreats, this.o.music.citiesLost, this.ended));
    if (!this.linkDown && !this.paused) this.updateBraceArms();
    store.aim = this.input!.aim;
    store.placement = this.input!.placement;
    store.cursorCue = this.cursorCue(tick);
    const out = this.renderer.render(store, this.pal!, this.cam, tick, t, ov);
    this.input!.lastScene = out;
    if (import.meta.env.DEV) {
      const w = window as unknown as { __saFrames?: number; __saTick?: number };
      w.__saFrames = (w.__saFrames ?? 0) + 1;
      // the display tick of this frame (motion traces in scripts/navy.ts)
      w.__saTick = tick;
    }
    this.refreshNation(t);
    this.store!.diplomacy.prune(tick);
    this.alerts!.extraAction = this.samFocusOffer(t);
    this.updateStrikeOffer(t, tick);
    this.alerts!.guided = this.guide.showing;
    let alert = this.alerts!.current(tick, t);
    if (this.linkDown) {
      alert = this.linkLine(t);
      const d = this.linkDown;
      if (!d.closed && !d.dead && t - d.since > LOST_SCREEN_MS && t > d.quietUntil) this.showLost();
    }
    this.hud!.setPlacing(this.input!.mode.type === 'place' ? this.input!.mode.kind : null);
    this.hud!.update(store.view, store.allies, tick, alert, this.chip());
    this.hud!.setGuide(this.paused ? null : this.guide.update(store.view, tick, t, (step) => track('guide_step', { step, s: Math.round(tick / TICK_HZ) })));
    // the news (and offers to us, pinned with their buttons) as toasts; each sounds as it comes up
    const offers = offerCards(store.view, store.diplomacy, this.nameOf, (o, accept) => this.answerOffer(o, accept), this.offerPage, () => {
      this.offerPage++;
      this.o.sfx.click();
    });
    const toasts = this.alerts!.toasts.frame(t, offers);
    for (const c of toasts.entered) this.o.sfx.toast(c.tone, c.kind);
    this.hud!.updateToasts(toasts);
    // (the map's words keep out of the HUD's blocks next frame)
    store.hudBlocks = this.hud!.blocks(t);
    // a full-screen moment or modal over the map: the map's words go under it, not over it
    this.labelCanvas.classList.toggle('under', this.hud!.overlayUp);
    // last seconds of an inbound that hits something of ours: soft ticking
    const secs = store.inboundThreats.find((x) => x.stake && x.bracedSeconds === null)?.etaSeconds;
    if (secs !== undefined && secs <= 5) {
      const s = Math.ceil(secs);
      if (s !== this.lastTickSecond) {
        this.lastTickSecond = s;
        this.o.sfx.tick();
      }
    }
  }

  /** Keep the open nation menu current (countdowns, an offer answered, a nation knocked out). */
  private refreshNation(now: number): void {
    const n = this.nationAt;
    const open = this.hud?.nationOpen;
    if (!n || open !== n.player) {
      this.nationAt = null;
      return;
    }
    if (now - n.ms < NATION_MENU_EVERY_MS) return;
    n.ms = now;
    if (this.store!.eliminated.has(n.player) || this.store!.view.eliminated) {
      this.hud!.closeNation();
      return;
    }
    this.hud!.updateNation(this.nationModel(n.player, n.board));
  }

  /** Until our first push ever (`Alerts.pushCue`): "RMB · PUSH" next to the cursor over land we could push into. */
  private cursorCue(tick: number): GameStore['cursorCue'] {
    const store = this.store!;
    const hover = this.input!.hover();
    if (!hover || this.input!.mode.type !== 'idle' || !this.alerts!.pushCue || store.view.eliminated || this.alerts!.finalMinute(tick)) return null;
    const hex = landHexAt(getGeo(this.o.world), hover.x, hover.y);
    if (hex < 0 || store.view.owner[hex] === store.me) return null;
    return { x: hover.x, y: hover.y, text: 'RMB · PUSH' };
  }

  private onEnd(e: EndMessage): void {
    if (!this.store || this.ended) return;
    this.ended = true;
    this.store.end(e);
    this.guide.matchEnded();
    track('match_end', { s: this.matchSecs(), out: this.store.view.eliminated });
    // the final world behind the results: fog lifted, every scar, the whole map
    this.home(true);
    setTimeout(() => this.el.classList.add('ended'), 1200);
    setTimeout(() => this.exit({ end: e, me: this.store!.me, players: this.store!.view.players, matchTicks: this.matchTicks }), 1800);
  }

  private exit(r: SessionResult | null): void {
    this.o.onExit(r);
  }

  /** The match as far as we have played it (seconds of match time). */
  private matchSecs(): number {
    return Math.round((this.store?.view.tick ?? 0) / TICK_HZ);
  }

  /** The world is on screen: the match has started for us (once; a closed page from now on is a leave). */
  private trackStart(): void {
    if (this.tracked) return;
    this.tracked = true;
    track('match_start', { online: !this.o.link.local, guide: this.guide.showing, s: this.matchSecs() });
    this.offLeave = onLeave(() => {
      if (!this.ended) track('match_leave', { s: this.matchSecs(), via: 'close' });
    });
  }

  dispose(): void {
    this.offLeave();
    cancelAnimationFrame(this.raf);
    if (this.countdown) clearInterval(this.countdown);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.resizeObs.disconnect();
    this.input?.dispose();
    window.removeEventListener('sa:devlook', this.devLook);
    this.o.link.close();
    this.el.remove();
  }
}
